import { Injectable } from "@angular/core";
import { JoDetail } from "app/model/entities/jo-detail";
import { PoDetail } from "app/model/entities/po-detail";
import { PoHeader } from "app/model/entities/po-header";
import { JoStatusCode } from "app/model/enums/jo-status-code";
import { PoStatusCode } from "app/model/enums/po-status-code";
import _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { AuthService } from "./auth.service";
import { BusyService } from "./busy.service";
import { DateFns } from "./date-fns";
import { DbQueryService } from "./db-query.service";
import { DbSaveService } from "./db-save.service";
import { DialogWrapperService } from "./dialog-wrapper.service";
import { UnitOfWork } from "./unit-of-work";
import { ItemDetailStatusEnum } from "@/model/entities/enums";
import { ManufacturerProductTypeConfig } from "@/model/entities/manufacturer-product-type-config";

@Injectable({ providedIn: "root" })
export class JoUtilsService {
  constructor(
    public uow: UnitOfWork,
    public dbQueryService: DbQueryService,
    public dbSaveService: DbSaveService,
    public dialogWrapperService: DialogWrapperService,
    public authService: AuthService,
    public busyService: BusyService,
    private toastr: ToastrService
  ) {}

  async uncompleteBatch(joPullBatchId: number) {
    const joDetails = await this.dbQueryService.getJoDetailsByJoPullBatchId(joPullBatchId);
    if (joDetails.length === 0) {
      this.dialogWrapperService.showOkMessage("Unable to reverse this batch", "No Job Order Details found.");
      return { ok: false };
    }

    const somePodsHaveBeenSent = joDetails.some((jod) =>
      jod.poDetails.some((pod) => pod.poHeader != null && pod.poHeader.poStatusId !== PoStatusCode.NewUnposted)
    );
    if (somePodsHaveBeenSent) {
      this.dialogWrapperService.showOkMessage(
        "Unable to reverse this batch",
        `Some or all of the Job Order Details in this batch have been placed for order.`
      );
      return { ok: false };
    }

    const r = await this.dbQueryService.getInvoiceDetailsByJoPullBatchId(joPullBatchId);
    if (r.length > 0) {
      this.dialogWrapperService.showOkMessage(
        "Unable to reverse this batch",
        "Some or all of the Job Order Details in this batch have already been invoiced."
      );
      return { ok: false };
    }

    // don't allow uncomplete of any 'Completed' PO Details

    // Only Open/Process Complete job orders can be reversed. Closed job orders are left
    // untouched on both axes - status and their PoDetails.
    const joHeaders = _.uniq(joDetails.map((jod) => jod.joHeader));
    const joHeadersToRevert = joHeaders.filter((joh) => joh.joStatusId === JoStatusCode.OpenProcessComplete);
    if (joHeadersToRevert.length === 0) {
      this.dialogWrapperService.showOkMessage(
        "Unable to reverse this batch",
        "No Open/Process Complete Job Orders found in this batch."
      );
      return { ok: false };
    }
    const revertIdSet = new Set(joHeadersToRevert.map((joh) => joh.id));
    const joDetailsToRevert = joDetails.filter((jod) => revertIdSet.has(jod.joHeaderId));

    const ok = await this.dialogWrapperService.yesNoDialog("Uncomplete this batch", "Are you sure?");
    if (!ok) {
      return { ok: false };
    }
    this.toastr.info("Reversing this batch and deleting associated order detail records...");
    // // 2 steps
    // // Mark all JoHeader records that are part of this batch with the 'prev' status.
    // // Create a collection of PurchaseOrderDetail records
    joHeadersToRevert.forEach((joh) => {
      joh.joStatusId = JoStatusCode.OpenProcessIncomplete;
    });

    let podCount = 0;
    let pohIdSet = new Set<number>();
    joDetailsToRevert.forEach((jod) => {
      // Note: there can be more than one PurchaseOrderDetail for a single JoDetail
      jod.poDetails.forEach((pod) => {
        const poh = pod.poHeader;
        // Only undo New/Unposted Pod's
        if (poh == null || poh.poStatusId === PoStatusCode.NewUnposted) {
          if (poh != null) {
            pohIdSet.add(pod.poHeaderId);
          }
          pod.entityAspect.setDeleted();
          podCount++;
        }
      });
    });

    const sr = await this.dbSaveService.saveChanges();

    this.toastr.success(`Deleted ${podCount} Purchase Order Detail record(s)`);
    return { ok: true, pohIdSet };
  }

  async deleteJoHeaderFromBatch(joHeaderId: number) {
    const q = this.uow
      .createQuery(JoDetail, "JoDetails")
      .expand(["joHeader", "joHeader.joHeaderPull", "joHeader.joHeaderPull.joPullBatch", "itemDetails"])
      .where({ joHeaderId: joHeaderId });

    const joDetails = await q.execute();
    if (joDetails.length === 0) {
      return;
    }
    // collect these here because they may get lost during delete process
    const joHeader = joDetails[0].joHeader;

    // first unlink all of the itemDetail records
    joDetails.forEach((jod) => {
      jod.itemDetails.slice().forEach((itd) => {
        itd.itemDetailStatusId = ItemDetailStatusEnum.InInventory;
        itd.joDetailId = null;
      });
    });

    if (joHeader.joHeaderPull != null) {
      const joPullBatch = joHeader.joHeaderPull.joPullBatch;

      // Then delete the joHeaderPull record
      joHeader.joHeaderPull.entityAspect.setDeleted();

      // Then check if any joHeaders remain in batch and if not delete the entire batch
      if (joPullBatch.joHeaderPulls.length === 0) {
        joPullBatch.entityAspect.setDeleted();
      } else {
        joPullBatch.modTs = new Date(); // For OC purposes
      }
    } else {
      // TODO: not sure why we ever get here but....
      console.warn("JoHeader has no JoHeaderPull");
    }

    return await this.dbSaveService.saveChanges();
  }

  /**
   * Create PurchaseOrder records for all unresolved quantities in the given JoDetails.
   * Groups POs by manufacturer. Sets accountId on PoDetails for account-owned orders.
   * Does NOT call saveChanges() — caller is responsible for saving.
   *
   * All server lookups run before any entity is created, so the PoHeaders and PoDetails are built
   * in one synchronous block. Keep it that way: a save or rejectChanges that runs between two creates
   * commits part of the POs, or detaches a PoHeader whose temp key later PoDetails still carry
   * (the save then fails on FK_PoDetail_PoHeader).
   */
  async createPurchaseOrdersForJoDetails(joDetails: JoDetail[]): Promise<number> {
    const mptConfigMap = new Map<JoDetail, ManufacturerProductTypeConfig | null>();
    for (const jod of joDetails) {
      if (jod.getUnresolvedQty() > 0) {
        mptConfigMap.set(jod, await this.dbQueryService.findMatchingManufacturerProductTypeConfig(jod));
      }
    }

    let odCount = 0;
    const now = new Date();
    const poHeaderMap: Record<string, PoHeader> = {};

    for (const [jod, mptConfig] of mptConfigMap) {
      if (jod.getUnresolvedQty() > 0) {
        const manuf = jod.product.productType.manufacturer;
        const isAcctOwned = jod.joHeader.isAccountOwnedOrder;
        const mapKey = `${manuf.id}_${isAcctOwned}`;
        let poHeader = poHeaderMap[mapKey];
        if (poHeader == null) {
          poHeader = this.uow.createEntity(PoHeader, {
            shippingWarehouseId: 1,
            manufacturerId: manuf.id,
            poStatusId: PoStatusCode.NewUnposted,
            crtnTs: now,
          });
          poHeaderMap[mapKey] = poHeader;
        }

        const leadDays = manuf.leadTimeDays;
        const podStruct = {} as PoDetail;
        podStruct.poHeaderId = poHeader.id;
        podStruct.joDetailId = jod.id;
        podStruct.accountId = jod.joHeader.isAccountOwnedOrder ? jod.joHeader.accountId : null;
        podStruct.productId = jod.productId;
        podStruct.origProductId = jod.productId;
        podStruct.poDetailTypeId = "ORDER";
        podStruct.orderQty = jod.getUnresolvedQty();
        podStruct.crtnTs = now;
        podStruct.expectedShipDate = DateFns.dateAdd(now, leadDays, "days");
        podStruct.manufacturerProductTypeConfigId = mptConfig?.id;

        this.uow.createEntity(PoDetail, podStruct);
        odCount++;
      }
    }

    return odCount;
  }
}
